import {
  Box,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography
} from '@mui/material';
import { useRuleExecutions } from '../../api/useUnwinding';
import { UnwindingExecution } from '../../types/unwinding.types';

const formatUsd = (value: number | undefined) => {
  if (value == null || isNaN(value)) return '-';
  return '$' + value.toLocaleString(undefined, { maximumFractionDigits: 2 });
};

const formatPercent = (value: number | undefined) => {
  if (value == null || isNaN(value)) return '-';
  return value.toFixed(2) + '%';
};

const formatTime = (iso: string) => {
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  });
};

const statusColor = (
  status: UnwindingExecution['overallStatus']
): 'success' | 'warning' | 'error' | 'info' | 'default' => {
  switch (status) {
    case 'complete':
      return 'success';
    case 'partial':
      return 'warning';
    case 'failed':
      return 'error';
    case 'pending_counter':
      return 'info';
    case 'maker_only':
      return 'warning';
    default:
      return 'default';
  }
};

const directionLabel = (dir: UnwindingExecution['direction']) =>
  dir === 'kalshi_maker' ? 'Kalshi Maker' : 'Poly Maker';

const cellSx = { fontSize: '0.75rem', py: 0.5, px: 1 } as const;
const headerSx = {
  fontSize: '0.65rem',
  fontWeight: 600,
  color: 'grey.500',
  py: 0.5,
  px: 1,
  textTransform: 'uppercase' as const,
  letterSpacing: '0.03em'
};

interface Props {
  ruleId: string;
}

export const ExecutionLogTable = ({ ruleId }: Props) => {
  const { data: executions, isLoading } = useRuleExecutions(ruleId);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
        <CircularProgress size={20} />
      </Box>
    );
  }

  if (!executions || executions.length === 0) {
    return (
      <Box sx={{ py: 2, textAlign: 'center' }}>
        <Typography sx={{ fontSize: '0.8rem', color: 'grey.500' }}>
          No executions yet
        </Typography>
      </Box>
    );
  }

  return (
    <Table size="small" sx={{ backgroundColor: 'rgba(255,255,255,0.02)' }}>
      <TableHead>
        <TableRow>
          <TableCell sx={headerSx}>Time</TableCell>
          <TableCell sx={headerSx}>Direction</TableCell>
          <TableCell sx={headerSx} align="right">
            Maker Price
          </TableCell>
          <TableCell sx={headerSx} align="right">
            Taker Price
          </TableCell>
          <TableCell sx={headerSx} align="right">
            Contracts
          </TableCell>
          <TableCell sx={headerSx} align="right">
            Volume USD
          </TableCell>
          <TableCell sx={headerSx} align="right">
            TNA%
          </TableCell>
          <TableCell sx={headerSx} align="right">
            Capital Recovered
          </TableCell>
          <TableCell sx={headerSx} align="center">
            Status
          </TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {executions.map(exec => (
          <TableRow
            key={exec._id}
            sx={{ '&:hover': { backgroundColor: 'rgba(255,255,255,0.03)' } }}
          >
            <TableCell sx={cellSx}>{formatTime(exec.createdAt)}</TableCell>
            <TableCell sx={cellSx}>{directionLabel(exec.direction)}</TableCell>
            <TableCell sx={cellSx} align="right">
              {exec.makerLeg.price.toFixed(4)}
            </TableCell>
            <TableCell sx={cellSx} align="right">
              {exec.takerLeg.price.toFixed(4)}
            </TableCell>
            <TableCell sx={cellSx} align="right">
              {exec.makerLeg.contracts}
            </TableCell>
            <TableCell sx={cellSx} align="right">
              {formatUsd(exec.totalVolumeUsd)}
            </TableCell>
            <TableCell sx={cellSx} align="right">
              {formatPercent(exec.effectiveTnaPercent)}
            </TableCell>
            <TableCell sx={cellSx} align="right">
              {formatUsd(exec.capitalRecovered)}
            </TableCell>
            <TableCell sx={cellSx} align="center">
              <Tooltip title={exec.errorMessage || ''} arrow>
                <Chip
                  label={exec.overallStatus.replace('_', ' ')}
                  size="small"
                  color={statusColor(exec.overallStatus)}
                  sx={{
                    fontSize: '0.6rem',
                    fontWeight: 600,
                    height: 18,
                    borderRadius: '3px',
                    textTransform: 'capitalize'
                  }}
                />
              </Tooltip>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};
